import { useEffect, useRef, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { User, Bell, Lock, Palette, Save, Sun, Moon, Check, Upload, Trash2, Square } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTenant, useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";

const COLOR_PRESETS = [
  { value: "#2563eb", name: "Blue" },
  { value: "#0f172a", name: "Slate" },
  { value: "#10b981", name: "Emerald" },
  { value: "#8b5cf6", name: "Violet" },
  { value: "#f59e0b", name: "Amber" },
  { value: "#ef4444", name: "Red" },
];

const MAX_LOGO_BYTES = 1024 * 1024; // 1MB

export default function Settings() {
  const { user } = useAuth();
  const { tenant, saveTenant, resetUserTheme } = useTenant();
  const { setTheme } = useTheme();
  const [name, setName] = useState(user?.name || "Demo User");
  const [email, setEmail] = useState(user?.email || "demo@demo.com");
  const [notifEmail, setNotifEmail] = useState(true);
  const [notifPush, setNotifPush] = useState(false);
  const [notifWeekly, setNotifWeekly] = useState(true);

  // Customization local draft
  const [workspaceDraft, setWorkspaceDraft] = useState(tenant.workspaceName);
  const [colorDraft, setColorDraft] = useState(tenant.primaryColor);
  const [themeDraft, setThemeDraft] = useState(tenant.defaultTheme);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setWorkspaceDraft(tenant.workspaceName);
    setColorDraft(tenant.primaryColor);
    setThemeDraft(tenant.defaultTheme);
  }, [tenant]);

  const previewColor = (c) => {
    setColorDraft(c);
    saveTenant({ primaryColor: c });
  };

  const previewTheme = (t) => {
    setThemeDraft(t);
    setTheme(t);
  };

  const handleLogoFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG, SVG).");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("Logo too large — max 1 MB for in-browser storage.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result;
      if (typeof dataUrl === "string") {
        try {
          saveTenant({ logo: dataUrl });
          toast.success("Logo uploaded");
        } catch (_err) {
          toast.error("Couldn't save logo — storage is full.");
        }
      }
    };
    reader.onerror = () => toast.error("Failed to read file.");
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    saveTenant({ logo: "" });
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast.message("Logo removed");
  };

  const saveCustomization = () => {
    saveTenant({
      workspaceName: workspaceDraft.trim() || "Your Business Portal",
      primaryColor: colorDraft,
      defaultTheme: themeDraft,
    });
    resetUserTheme();
    setTheme(themeDraft);
    toast.success("Tenant customization saved");
  };

  const resetCustomization = () => {
    saveTenant({
      workspaceName: "Your Business Portal",
      primaryColor: "#2563eb",
      defaultTheme: "light",
      logo: "",
      onboarded: true,
    });
    resetUserTheme();
    setTheme("light");
    if (fileInputRef.current) fileInputRef.current.value = "";
    toast.message("Reset to defaults");
  };

  const replayOnboarding = () => {
    saveTenant({ onboarded: false });
    toast.message("Onboarding will replay");
    window.location.assign("/onboarding");
  };

  return (
    <div className="space-y-8" data-testid="settings-page">
      <PageHeader
        title="Settings & Preferences"
        subtitle="Manage your profile, notifications, and account preferences."
      />

      <Tabs defaultValue="profile" className="w-full">
        <TabsList
          data-testid="settings-tabs"
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-1 h-auto rounded-lg"
        >
          <TabsTrigger
            value="profile"
            data-testid="tab-profile"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md px-4 py-2"
          >
            <User className="h-3.5 w-3.5 mr-2" />
            Profile
          </TabsTrigger>
          <TabsTrigger
            value="notifications"
            data-testid="tab-notifications"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md px-4 py-2"
          >
            <Bell className="h-3.5 w-3.5 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger
            value="security"
            data-testid="tab-security"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md px-4 py-2"
          >
            <Lock className="h-3.5 w-3.5 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger
            value="customization"
            data-testid="tab-customization"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md px-4 py-2"
          >
            <Palette className="h-3.5 w-3.5 mr-2" />
            Customization
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <Card className="p-6 md:p-8 border border-slate-200 dark:border-slate-800 rounded-xl elev-1 max-w-2xl">
            <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
              Profile information
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="sname" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Name</Label>
                <Input id="sname" data-testid="settings-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 rounded-lg" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="semail" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Email</Label>
                <Input id="semail" data-testid="settings-email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11 rounded-lg" />
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button data-testid="save-profile" onClick={() => toast.success("Profile saved (demo)")} className="bg-primary hover:bg-primary/90 rounded-lg btn-premium">
                <Save className="h-4 w-4 mr-2" />
                Save Profile
              </Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <Card className="p-6 md:p-8 border border-slate-200 dark:border-slate-800 rounded-xl elev-1 max-w-2xl">
            <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
              Notification preferences
            </div>
            <div className="mt-6 divide-y divide-slate-100 dark:divide-slate-800">
              <div className="py-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Email notifications</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Task reminders, client updates, order changes.</div>
                </div>
                <Switch checked={notifEmail} onCheckedChange={setNotifEmail} data-testid="switch-email" />
              </div>
              <div className="py-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Push notifications</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">Realtime alerts on your devices.</div>
                </div>
                <Switch checked={notifPush} onCheckedChange={setNotifPush} data-testid="switch-push" />
              </div>
              <div className="py-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">Weekly summary</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">A snapshot of your business every Monday.</div>
                </div>
                <Switch checked={notifWeekly} onCheckedChange={setNotifWeekly} data-testid="switch-weekly" />
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <Card className="p-6 md:p-8 border border-slate-200 dark:border-slate-800 rounded-xl elev-1 max-w-2xl">
            <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
              Password & security
            </div>
            <div className="mt-6 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="cp" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Current password</Label>
                <Input id="cp" type="password" data-testid="current-password" placeholder="••••••••" className="h-11 rounded-lg" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="np" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">New password</Label>
                  <Input id="np" type="password" data-testid="new-password" placeholder="••••••••" className="h-11 rounded-lg" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnp" className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">Confirm new password</Label>
                  <Input id="cnp" type="password" data-testid="confirm-password" placeholder="••••••••" className="h-11 rounded-lg" />
                </div>
              </div>
              <div className="pt-2 flex justify-end">
                <Button data-testid="save-security" onClick={() => toast.success("Password updated (demo)")} className="bg-primary hover:bg-primary/90 rounded-lg btn-premium">
                  Update Password
                </Button>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="customization" className="mt-6">
          <Card className="p-6 md:p-8 border border-slate-200 dark:border-slate-800 rounded-xl elev-1 max-w-2xl">
            <div className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.2em]">
              Portal customization
            </div>
            <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
              Rebrand this portal for any business. Workspace name, primary color, and default theme are saved per-tenant and persist across sessions.
            </p>

            <div className="mt-6 space-y-6">
              {/* Logo upload */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">
                  Logo
                </Label>
                <div className="flex items-center gap-4">
                  <div
                    className={`h-16 w-16 rounded-xl flex items-center justify-center overflow-hidden shrink-0 ${
                      tenant.logo
                        ? "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                        : "bg-primary"
                    }`}
                    data-testid="logo-preview"
                  >
                    {tenant.logo ? (
                      <img
                        src={tenant.logo}
                        alt="Tenant logo"
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <Square className="h-7 w-7 text-white" fill="white" />
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleLogoFile(e.target.files?.[0])}
                      className="hidden"
                      data-testid="logo-file-input"
                    />
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        data-testid="upload-logo-button"
                        className="rounded-lg"
                      >
                        <Upload className="h-3.5 w-3.5 mr-2" />
                        {tenant.logo ? "Replace logo" : "Upload logo"}
                      </Button>
                      {tenant.logo && (
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={removeLogo}
                          data-testid="remove-logo-button"
                          className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg"
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-2" />
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      PNG, JPG, or SVG · up to 1 MB · stored in your browser
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">
                  Workspace name
                </Label>
                <Input
                  value={workspaceDraft}
                  onChange={(e) => setWorkspaceDraft(e.target.value)}
                  data-testid="custom-workspace"
                  className="h-11 rounded-lg max-w-md"
                  placeholder="Your Business Portal"
                />
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Shown on the sidebar and login screen.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">
                  Default theme
                </Label>
                <div className="flex items-center gap-2" data-testid="default-theme-group">
                  {[
                    { value: "light", label: "Light", icon: Sun },
                    { value: "dark", label: "Dark", icon: Moon },
                  ].map((opt) => {
                    const active = themeDraft === opt.value;
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`default-theme-${opt.value}`}
                        onClick={() => previewTheme(opt.value)}
                        className={`flex items-center gap-2 px-4 h-10 rounded-lg border text-sm font-medium transition-all ${
                          active
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 hover:border-slate-400 dark:hover:border-slate-600"
                        }`}
                      >
                        <Icon className="h-4 w-4" strokeWidth={1.75} />
                        {opt.label}
                        {active && <Check className="h-3.5 w-3.5 ml-1" />}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Initial theme each user sees when they open this portal.
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-700 dark:text-slate-300 font-medium">
                  Primary color
                </Label>
                <div className="flex items-center gap-3 flex-wrap" data-testid="primary-color-group">
                  {COLOR_PRESETS.map((c) => {
                    const active = colorDraft.toLowerCase() === c.value.toLowerCase();
                    return (
                      <button
                        key={c.value}
                        type="button"
                        data-testid={`color-${c.value}`}
                        onClick={() => previewColor(c.value)}
                        title={c.name}
                        className={`relative h-10 w-10 rounded-lg border transition-all hover:scale-110 ${
                          active
                            ? "border-slate-900 dark:border-white ring-2 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 ring-slate-900 dark:ring-white"
                            : "border-slate-200 dark:border-slate-700"
                        }`}
                        style={{ backgroundColor: c.value }}
                      >
                        {active && (
                          <Check className="h-4 w-4 absolute inset-0 m-auto text-white drop-shadow" />
                        )}
                      </button>
                    );
                  })}
                  <div className="flex items-center gap-2 ml-2">
                    <Input
                      type="color"
                      value={colorDraft}
                      onChange={(e) => previewColor(e.target.value)}
                      data-testid="custom-color-picker"
                      className="h-10 w-14 p-1 rounded-lg cursor-pointer"
                    />
                    <code className="text-xs font-mono text-slate-500 dark:text-slate-400">
                      {colorDraft}
                    </code>
                  </div>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Applied live across buttons, charts, links, and active nav.
                </p>
              </div>
            </div>

            <div className="mt-8 flex justify-between items-center pt-6 border-t border-slate-100 dark:border-slate-800">
              <Button
                variant="ghost"
                data-testid="reset-customization"
                onClick={resetCustomization}
                className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              >
                Reset to defaults
              </Button>
              <Button
                data-testid="save-customization"
                onClick={saveCustomization}
                className="bg-primary hover:bg-primary/90 rounded-lg btn-premium"
              >
                <Save className="h-4 w-4 mr-2" />
                Save Tenant Settings
              </Button>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

